import { useCallback, useEffect, useState } from "react";
import { JobApplication } from "../Entities/JobApplication";
import { CandidateJobApplicationFilters, PaginationType } from "../Interface";
import jobApplicationService from "../Service/job.application.service";

const defaultPagination: PaginationType = {
  page: 1,
  limit: 10,
  totalPages: 0,
  totalRecords: 0,
};

const defaultFilters: CandidateJobApplicationFilters = {
  search: null,
  sort: null,
  source: null,
  profileMatchStatus: null,
  assessmentStatus: null,
  status: null,
  onlineInterviewStatus: null,
};

const useCandidateJobApplications = (candidateId: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [jobApplications, setJobApplications] = useState<JobApplication[]>([]);
  const [pagination, setPagination] =
    useState<PaginationType>(defaultPagination);
  const [filters, setFilters] =
    useState<CandidateJobApplicationFilters>(defaultFilters);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleFilter = (
    key: keyof CandidateJobApplicationFilters,
    value: any,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleClearFilter = () => {
    setFilters(defaultFilters);
    setPagination(defaultPagination);
  };

  const {
    search,
    sort,
    source,
    onlineInterviewStatus,
    profileMatchStatus,
    assessmentStatus,
    status,
  } = filters;
  const { page, limit } = pagination;

  const getCandidateApplications = useCallback(async () => {
    if (!candidateId) {
      return;
    }
    try {
      setLoading(true);

      const candidateApplicationsResponse =
        await jobApplicationService.getAllCandidateApplications(
          candidateId,
          {
            sort,
            source,
            search,
            profileMatchStatus,
            assessmentStatus,
            status,
            onlineInterviewStatus,
          },
          page,
          limit,
        );

      if (candidateApplicationsResponse) {
        setJobApplications(candidateApplicationsResponse?.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: candidateApplicationsResponse.totalRecords || 0,
          totalPages: candidateApplicationsResponse.totalPages || 0,
        }));
      } else {
        console.error(
          "~ useCandidateJobApplications ~ Inavlid candidateApplicationsResponse:",
          candidateApplicationsResponse,
        );
        setJobApplications([]);
        setPagination((prev) => ({
          ...prev,
          totalRecords: 0,
          totalPages: 0,
        }));
      }
    } catch (error) {
      console.error(
        "~ useCandidateJobApplications - getCandidateApplications ~ error:",
        error,
      );
      setJobApplications([]);
      setPagination((prev) => ({
        ...prev,
        totalRecords: 0,
        totalPages: 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [
    candidateId,
    page,
    limit,
    sort,
    source,
    search,
    profileMatchStatus,
    assessmentStatus,
    onlineInterviewStatus,
    status,
    setLoading,
  ]);

  useEffect(() => {
    getCandidateApplications();
  }, [getCandidateApplications]);

  return {
    loading,
    jobApplications,
    pagination,
    filters,
    handlePageChange,
    handleFilter,
    handleClearFilter,
  };
};

export default useCandidateJobApplications;
